import type { DesignBrief } from "./design-brief.js";
import type { SiteBlueprint } from "../types.js";

export type DesignFamilyId =
  | "modern-blue-v1"
  | "modern-sage-v1"
  | "modern-soft-pink-v1"
  | "modern-navy-v1"
  | "modern-charcoal-v1";

export interface DesignFamilySelection {
  familyId: DesignFamilyId;
  scores: Record<DesignFamilyId, number>;
  reasons: string[];
}

function clamp(value: number): number {
  return Number(Math.max(0, Math.min(1, value)).toFixed(2));
}

export function selectDesignFamily(
  blueprint: SiteBlueprint,
  brief: DesignBrief,
): DesignFamilySelection {
  const sections = blueprint.sections ?? [];

  const imageCount = [
    ...(blueprint.images ?? []),
    ...sections.flatMap((section) => section.images ?? []),
  ].filter(
    (image, index, images) =>
      Boolean(image.src) &&
      images.findIndex((candidate) => candidate.src === image.src) === index,
  ).length;

  const galleryCount = sections.filter(
    (section) =>
      section.type === "gallery" ||
      /gallery|portfolio|projects?|our work/i.test(section.heading ?? ""),
  ).length;

  const textLength = sections.reduce(
    (total, section) =>
      total +
      (section.heading?.length ?? 0) +
      section.paragraphs.reduce((sum, paragraph) => sum + paragraph.length, 0),
    0,
  );

  const serviceCount = sections.filter(
    (section) => section.type === "services",
  ).length;

  let blue = 0.42;
  let sage = 0.28;
  let softPink = 0.22;
  let navy = 0.3;
  let charcoal = 0.25;

  const reasons: string[] = [];

  if (brief.businessArchetype === "service-business") {
    blue += 0.18;
    navy += 0.16;

    reasons.push(
      "Service-business evidence favors the Blue or Navy Modern Business themes.",
    );
  }

  if (
    brief.businessArchetype === "creative-studio" ||
    brief.businessArchetype === "retail"
  ) {
    softPink += 0.2;
    charcoal += 0.16;

    reasons.push(
      "Creative or retail evidence favors the Soft Pink or Charcoal themes.",
    );
  }

  if (
    brief.businessArchetype === "professional-services" ||
    brief.businessArchetype === "wellness-consulting"
  ) {
    sage += 0.2;
    navy += 0.14;

    reasons.push(
      "Professional or consulting evidence favors the Sage or Navy themes.",
    );
  }

  if (
    brief.mediaEmphasis === "dominant" ||
    imageCount >= 12 ||
    galleryCount > 0
  ) {
    charcoal += 0.16;
    softPink += 0.1;

    reasons.push(
      "Strong image and gallery evidence favors visually expressive Modern Business themes.",
    );
  }

  if (brief.mediaEmphasis === "minimal" || textLength > 1800) {
    navy += 0.12;
    sage += 0.08;

    reasons.push(
      "Text-rich or low-media content favors restrained Navy or Sage themes.",
    );
  }

  if (brief.primaryGoal === "showcase-work") {
    charcoal += 0.14;
    softPink += 0.08;
  }

  if (brief.primaryGoal === "educate" || brief.primaryGoal === "build-trust") {
    sage += 0.12;
    navy += 0.1;
  }

  if (brief.primaryGoal === "request-quote" || serviceCount >= 2) {
    blue += 0.14;
    navy += 0.1;
  }

  const scores = {
    "modern-blue-v1": clamp(blue),
    "modern-sage-v1": clamp(sage),
    "modern-soft-pink-v1": clamp(softPink),
    "modern-navy-v1": clamp(navy),
    "modern-charcoal-v1": clamp(charcoal),
  } satisfies Record<DesignFamilyId, number>;

  const familyId =
    (Object.entries(scores) as Array<[DesignFamilyId, number]>).sort(
      (first, second) => second[1] - first[1],
    )[0]?.[0] ?? "modern-blue-v1";

  return {
    familyId,
    scores,
    reasons,
  };
}
